/**
 * useRailPins — per-user left-rail favorites store (feat/rail-redesign-v2 P2).
 *
 * A module-level store (not a React context) so the rail in the frozen AppShell
 * and every PinButton scattered across surfaces stay in sync without threading a
 * provider through App.tsx/AppShell.tsx. Standalone axios instance per the
 * proposalsApi/clientHubApi precedent (services/api.ts stays frozen).
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api', withCredentials: true });

// 'nav' = a starred rail item (Favorites quick-menu); the others are deep surfaces.
export type RailSurfaceType = 'pipeline' | 'campaign' | 'board' | 'client' | 'nav';

export interface RailPin {
  id: number;
  surface_type: RailSurfaceType;
  surface_id: string;
  label: string;
  parent_group: string;
  created_at: string;
}

export interface PinInput {
  surface_type: RailSurfaceType;
  surface_id: string | number;
  label: string;
  parent_group: string;
}

// Shared store.
let pins: RailPin[] = [];
let loaded = false;
let inFlight: Promise<void> | null = null;
const subs = new Set<() => void>();
const notify = () => subs.forEach((cb) => cb());

async function fetchPins(): Promise<void> {
  try {
    const r = await api.get('/rail-pins');
    pins = (r.data?.data ?? []) as RailPin[];
  } catch {
    pins = [];
  }
  loaded = true;
  notify();
}

export function loadRailPins(force = false): Promise<void> {
  if (loaded && !force) return Promise.resolve();
  if (inFlight) return inFlight;
  inFlight = fetchPins().finally(() => { inFlight = null; });
  return inFlight;
}

export async function pinSurface(p: PinInput): Promise<void> {
  await api.post('/rail-pins', { ...p, surface_id: String(p.surface_id) });
  await fetchPins();
}

export async function unpinSurface(type: RailSurfaceType, id: string | number): Promise<void> {
  await api.delete('/rail-pins', { params: { surface_type: type, surface_id: String(id) } });
  await fetchPins();
}

function subscribe(cb: () => void): () => void {
  subs.add(cb);
  return () => { subs.delete(cb); };
}

/** Subscribe to the current user's pins (loads on first mount). */
export function useRailPins(): { pins: RailPin[]; loaded: boolean } {
  const [, force] = useState(0);
  useEffect(() => {
    const unsub = subscribe(() => force((n) => n + 1));
    loadRailPins();
    return unsub;
  }, []);
  return { pins, loaded };
}

/** Pin state + toggle for one surface, for the PinButton. */
export function usePinState(type: RailSurfaceType, id: string | number): {
  pinned: boolean;
  busy: boolean;
  toggle: (input: Omit<PinInput, 'surface_type' | 'surface_id'>) => Promise<void>;
} {
  const { pins: all } = useRailPins();
  const [busy, setBusy] = useState(false);
  const sid = String(id);
  const pinned = all.some((p) => p.surface_type === type && p.surface_id === sid);
  const toggle = useCallback(async (input: Omit<PinInput, 'surface_type' | 'surface_id'>) => {
    setBusy(true);
    try {
      if (pinned) await unpinSurface(type, sid);
      else await pinSurface({ surface_type: type, surface_id: sid, ...input });
    } finally {
      setBusy(false);
    }
  }, [pinned, type, sid]);
  return { pinned, busy, toggle };
}

// ── Phase 5: per-user rail order (drag-reorder), server-saved ───────────────
export interface RailLayout {
  group_order: string[];
  item_order: Record<string, string[]>;
}

let layout: RailLayout = { group_order: [], item_order: {} };
let layoutLoaded = false;
let layoutInFlight: Promise<void> | null = null;
// Monotonic save version. A failed PUT only reverts if it's still the LATEST
// save — otherwise a stale failure would clobber a newer optimistic drag.
let layoutVersion = 0;
const layoutSubs = new Set<() => void>();
const notifyLayout = () => layoutSubs.forEach((cb) => cb());

async function fetchLayout(): Promise<void> {
  try {
    const r = await api.get('/rail-pins/layout');
    const data = r.data?.data ?? {};
    layout = {
      group_order: Array.isArray(data.group_order) ? data.group_order : [],
      item_order: data.item_order && typeof data.item_order === 'object' ? data.item_order : {},
    };
  } catch {
    layout = { group_order: [], item_order: {} };
  }
  layoutLoaded = true;
  notifyLayout();
}

export function loadRailLayout(force = false): Promise<void> {
  if (layoutLoaded && !force) return Promise.resolve();
  if (layoutInFlight) return layoutInFlight;
  layoutInFlight = fetchLayout().finally(() => { layoutInFlight = null; });
  return layoutInFlight;
}

/**
 * Optimistically set the layout locally, then persist. On failure it reverts —
 * but ONLY if no later save has superseded this one (version guard), so a slow
 * failing PUT can't clobber a subsequent drag the user already made.
 */
export async function saveRailLayout(next: RailLayout): Promise<void> {
  const myVersion = ++layoutVersion;
  const prev = layout;
  layout = next;
  notifyLayout();
  try {
    await api.put('/rail-pins/layout', next);
  } catch {
    if (myVersion === layoutVersion) {
      // still the newest save → safe to revert this optimistic update
      layout = prev;
      notifyLayout();
    }
    // else a newer save already owns the state; leave it alone
  }
}

/** Subscribe to the current user's rail order (loads on first mount). */
export function useRailLayout(): RailLayout {
  const [, force] = useState(0);
  useEffect(() => {
    const sub = () => force((n) => n + 1);
    layoutSubs.add(sub);
    loadRailLayout();
    return () => { layoutSubs.delete(sub); };
  }, []);
  return layout;
}
